import { type ImpersonatorSession, type UserSession } from '@solinkcorp/authentication';
import { auth } from '@solinkcorp/vd-common';
import { type AuthLocation } from '@solinkcorp/vd-common/lib/esm/auth/authUtils';
import AuthSession from './AuthSession';

/**
 * Wrapper that handles Authentication along with token renewal and refresh
 */
class AuthService {
  private _userSession?: AuthSession;
  private _impersonatorSession?: AuthSession;
  private _user: UserSession | undefined;

  setCurrentUser(user: UserSession, impersonator: ImpersonatorSession | undefined) {
    this._user = user;
    this._userSession = new AuthSession(user);
    this._impersonatorSession = impersonator ? new AuthSession(impersonator) : undefined;
  }

  /**
   * @deprecated - use `useUserSession().thirdPartyCookiesSet` instead.
   */
  get cookiesSet(): boolean {
    return !!this._user?.thirdPartyCookiesSet;
  }

  /**
   * @deprecated - use `!useUserSession().thirdPartyCookiesSet` instead.
   */
  get usePreflight(): boolean {
    return !this._user?.thirdPartyCookiesSet;
  }

  /**
   * Get the current session
   * @deprecated - use `useUserSession()` instead.
   */
  get session(): AuthSession | unknown {
    return this.getSession() || {};
  }

  /**
   * Get the current auth session.
   * @deprecated - use `useUserSession()` instead.
   */
  getSession(): AuthSession | undefined {
    return this._userSession || this._impersonatorSession;
  }

  /**
   * Get the impersonator Session
   * @deprecated - use `useImpersonator()` instead.
   */
  get impersonator() {
    return this._impersonatorSession;
  }

  /**
   * Get the current Auth Token
   * @deprecated - use `useUserSession().jwtToken` instead.
   */
  get authToken() {
    const session = this.getSession();
    if (!session) {
      throw new Error(`Attempting to use AuthToken when not LoggedIn`);
    }
    return session.authToken;
  }

  /**
   * Check to see if the current user is logged in and has a valid session
   * @deprecated
   */
  get isLoggedIn() {
    return !!this._userSession;
  }

  /**
   * Check to see if the user is impersonating someone else
   * @deprecated - use `!!useImpersonator()` instead.
   */
  get isImpersonating() {
    return !!this._impersonatorSession;
  }

  /**
   * Check to see if the user is a guest
   * @deprecated - use `useUserSession().type === 'guest'` instead.
   */
  get isGuest(): boolean {
    return this._user?.type === 'guest';
  }

  /**
   * Returns a `[url, options]` pair you can pass directly to `fetch()` to send
   * an authenticated request to the given location.
   *
   * @param location - The location to send a request to.
   * @param url - URL to fetch.  Can be an absolute URL (e.g. "https://...") or
   *   a relative url that starts with a "/" (e.g. "/cameras/cameraId/thumb.jpg").
   * @param options - Any valid options you could pass to `fetch(url, options)`.
   */
  getLocalFetchParams(
    location: AuthLocation,
    url: string,
    options?: RequestInit,
  ): [string, RequestInit] {
    const session = this.getSession();
    if (!session) {
      throw new Error(`Can't get local fetch credentials before we've logged in.`);
    }
    const jwt = session.authToken;

    // Use cookies only if cookies are set and `USE_PRE_FLIGHT` is unset or is false.
    const useCookies = this.cookiesSet;
    return auth.getLocalFetchParams(location, jwt, useCookies, url, options);
  }
}

export default new AuthService();

// Exported for unit testing.
export { AuthService as _AuthService };
